import { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Area, Bar, Line } from "recharts";
import { DASH_PATTERN } from "@plannel/components/rechart/ChartComposed";
import { cloneDeep, divide, floor, isEmpty, isEqual, sumBy, values } from "lodash";
import BarChartIcon from "@mui/icons-material/BarChart";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddHomeIcon from "@mui/icons-material/AddHome";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import SettingsIcon from "@mui/icons-material/Settings";

import Card from "@plannel/components/dashboard/DashboardCard";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import { DashboardComposedChart } from "@plannel/components/dashboard/DashboardChart";
import SeriesLegend from "@plannel/components/dashboard/SeriesLegend";
import { makeFmtMoney } from "@plannel/components/dashboard/dashboardFormat";
import ChartConfigModal from "@plannel/components/modal/ChartConfigModal";
import Snackbar from "@plannel/components/Snackbar";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import dashboardService from "@plannel/services/dashboard/dashboard-service";
import useDashboardP13n from "@plannel/hooks/useDashboardP13n";
import numberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import axiosUtil from "@plannel/utils/axios-bigint";
import debug from "@plannel/utils/debug-util";
import { FS } from "@plannel/utils/font-util";

/*
 * Integrated Dashboard — Sales / Gross Profit / Purchase / Inventory 4개 도메인.
 * DW 대시보드 표준 룩앤필(dw-dashboard 스킬): 상단 KPI 스코어보드(KpiCard) + 하단 4개 트렌드 차트.
 *   · 차트: DashboardComposedChart(recharts) + ColorUtil 시리즈색, 시리즈 타입 BAR/LINE/AREA(설정 모달) 지원.
 *   · Sales/GP 의 Projected(landing) 라인은 월별 막대와 스케일 차이가 커 우측 축으로 분리(막대 가독성 확보).
 * 데이터 소스(dashboardService.get)와 KPI 계산식은 기존 로직 그대로 유지한다.
 */

const MAIN = "main";     // 좌측 축(금액)
const RIGHT = "right";   // 우측 축(비율/회전율/대형 라인)
// 차트 타입 4종 — 앱 전역 규약(DW 대시보드 DW_CHART_TYPES 와 같은 목록).
//   DASHED = 점선. 목표·기준선 성격 시리즈를 실적선과 눈으로 구분할 때 쓴다.
const CHART_TYPES = ["BAR", "LINE", "AREA", "DASHED"];

// 섹션별 시리즈 정의 — name 은 백엔드 measure 키(라벨은 t(name)), type 은 기본 차트타입, axis=RIGHT 는 우측 축.
//   financeGated: Finance 데이터가 없으면(합계 0) 숨김(기존 Dashboard 규칙과 동일).
const SECTIONS = [
  {
    key: CodeUtil.dashboardTitle.SALES,
    icon: <BarChartIcon sx={{ fontSize: 17 }} />,
    sparkKey: "actSaleAmt",
    rightAxis: "money",
    series: [
      { name: "actSaleAmt", type: "AREA" },
      { name: "planSaleAmt", type: "LINE" },
      { name: "finanSaleAmt", type: "LINE", financeGated: true },
      { name: "landSaleAmt", type: "LINE", axis: RIGHT },
    ],
  },
  {
    key: CodeUtil.dashboardTitle.GROSS_PROFIT,
    icon: <AccountBalanceIcon sx={{ fontSize: 17 }} />,
    sparkKey: "actGroProfit",
    rightAxis: "money",
    series: [
      { name: "actGroProfit", type: "AREA" },
      { name: "demandPlanGP", type: "LINE" },
      { name: "finanGroProfit", type: "LINE", financeGated: true },
      { name: "landGroProfit", type: "LINE", axis: RIGHT },
    ],
  },
  {
    key: CodeUtil.dashboardTitle.PURCHASE,
    icon: <AddHomeIcon sx={{ fontSize: 17 }} />,
    sparkKey: "actPcAmt",
    rightAxis: "percent",
    series: [
      { name: "actPcAmt", type: "AREA" },
      { name: "purchasePlanAmt", type: "LINE" },
      { name: "purchaseBudgetAmt", type: "LINE" },
      { name: "planFillRate", type: "LINE", axis: RIGHT },
      { name: "budgetFillRate", type: "LINE", axis: RIGHT },
    ],
  },
  {
    key: CodeUtil.dashboardTitle.INVENTORY,
    icon: <Inventory2OutlinedIcon sx={{ fontSize: 17 }} />,
    sparkKey: "actStockAmt",
    rightAxis: "ratio",
    series: [
      { name: "actStockAmt", type: "AREA" },
      { name: "planStockAmt", type: "LINE" },
      { name: "targetInvAmt", type: "LINE" },
      { name: "actInvTurnover", type: "LINE", axis: RIGHT },
      { name: "planInvTurnover", type: "LINE", axis: RIGHT },
    ],
  },
];

// 시리즈별 기본 차트 설정(z_p13n config_obj.chart) — 정의된 type 을 기본 chartType 으로.
const DEFAULT_CHART_CONFIG = SECTIONS.reduce((acc, sec) => {
  sec.series.forEach((s) => { acc[s.name] = { chartType: s.type, activeFlg: true }; });
  return acc;
}, {});

// ── KPI 계산 헬퍼(기존 Dashboard.js 로직 그대로 — 순수 함수는 모듈 스코프로 승격) ──
const checkNumberValue = (num) => (isNaN(num) || num === Infinity || num === null ? 0 : num);

const getSum = (sumList) => {
  let sum = 0;
  if (!sumList || isEmpty(sumList)) return sum;
  for (const amt of sumList) {
    if (amt) sum = numberUtil.decimalAdd(sum, amt, 5);
  }
  return sum;
};

const getSumUntilCurrentMonth = (sumList, baseDates) => {
  let sum = 0;
  if (!sumList || isEmpty(sumList) || !baseDates || isEmpty(baseDates)) return sum;
  const now = new Date();
  const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  for (let i = 0; i < baseDates.length; i++) {
    const dateKey = baseDates[i].slice(0, 7);
    const value = sumList[i];
    if (dateKey <= nowKey && value) sum = numberUtil.decimalAdd(sum, value, 5);
  }
  return sum;
};

const getTotalDaysUntilMonth = (month) => {
  const currentYear = new ZDate().getFullYear();
  const daysInMonth = new ZDate(currentYear, month + 1, 0).getDate();
  let totalDays = 0;
  for (let m = 0; m < month; m++) {
    totalDays += new ZDate(currentYear, m + 1, 0).getDate();
  }
  totalDays += daysInMonth;
  return totalDays;
};

const findLastNonNullValue = (array) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i] !== null && array[i] !== undefined) return array[i];
  }
  return null;
};

const fmtStat = (v) => (Array.isArray(v) ? `${v[0]} · ${v[1]}d` : v);

// ─────────────────────────────────────────
// 하단 트렌드 차트 카드 — 헤더(아이콘+제목+확대/설정) + 차트 + 범례
// ─────────────────────────────────────────
const TrendCard = ({
  section, raw, periods, financeAvailable, fmtMoney, kpi,
  chartConfig, isActive, onToggleSeries, onOpenConfig, expanded, onToggleExpand,
}) => {
  const { t } = useTranslation();
  const stats = kpi?.stats ?? [];

  const series = useMemo(
    () => section.series.filter((s) => !(s.financeGated && !financeAvailable)),
    [section, financeAvailable],
  );

  // 피벗 배열(measure별 월 배열) → recharts 행 형식 [{ period, <measure>: v, ... }]
  const data = useMemo(
    () => periods.map((p, i) => {
      const row = { period: p };
      series.forEach((s) => { row[s.name] = raw?.[s.name]?.[i] ?? 0; });
      return row;
    }),
    [periods, series, raw],
  );

  const rightFmt = section.rightAxis === "percent"
    ? (v) => `${numberUtil.formatNumber(v, { maximumFractionDigits: 1 })}%`
    : section.rightAxis === "money"
      ? fmtMoney
      : (v) => numberUtil.formatNumber(v, { maximumFractionDigits: 1 });

  const yAxes = [{ id: MAIN, tickFormatter: fmtMoney }];
  if (section.rightAxis) yAxes.push({ id: RIGHT, orientation: "right", tickFormatter: rightFmt });

  const colorOf = (i) => ColorUtil.colorSet(i);
  const legendItems = series.map((s, i) => ({ key: s.name, label: t(s.name), color: colorOf(i) }));
  const rightLabels = new Set(series.filter((s) => s.axis === RIGHT).map((s) => t(s.name)));

  const gradId = (name) => `grad-${section.key}-${name}`;
  const typeOf = (s) => String(chartConfig?.[s.name]?.chartType ?? s.type).toUpperCase();

  // 시리즈 렌더 — 설정(chartType)에 따라 분기. Inventory2 감성: 영역=그라데이션, 선=얇게(dot 없음).
  const renderSeries = (s, i) => {
    const color = colorOf(i);
    const yAxisId = s.axis === RIGHT ? RIGHT : MAIN;
    const common = { key: s.name, yAxisId, dataKey: s.name, name: t(s.name) };
    const type = typeOf(s);
    if (type === "AREA") {
      return <Area {...common} type="monotone" stroke={color} strokeWidth={2} fill={`url(#${gradId(s.name)})`} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />;
    }
    if (type === "BAR") {
      return <Bar {...common} fill={color} barSize={10} radius={[2, 2, 0, 0]} />;
    }
    // LINE·DASHED 는 대시 패턴만 다르다. 패턴 값은 ChartComposed 가 단일 출처로 갖는다.
    return <Line {...common} type="monotone" stroke={color} strokeWidth={1.8}
      strokeDasharray={type === "DASHED" ? DASH_PATTERN : undefined}
      dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />;
  };

  return (
    <Card sx={{ flex: 1, minHeight: 0 }}>
      {/* 헤더 — 아이콘 + 제목 + 확대/설정 (KPI 는 상단 스코어보드로 이동) */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75, gap: 1, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: "primary.main", minWidth: 0 }}>
          {section.icon}
          <Typography sx={{ fontSize: FS.H1, fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}>
            {t(section.key)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, flexShrink: 0 }}>
          <IconButton size="small" onClick={onToggleExpand} sx={{ p: 0.25, color: "text.secondary" }}>
            {expanded ? <CloseFullscreenIcon sx={{ fontSize: 15 }} /> : <OpenInFullIcon sx={{ fontSize: 15 }} />}
          </IconButton>
          <IconButton size="small" onClick={onOpenConfig} sx={{ p: 0.25, color: "text.secondary" }}>
            <SettingsIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      </Box>

      {/* 본문 — 좌: KPI(대표값 + 보조 비율), 우: 트렌드 차트 (Option C) */}
      <Box sx={{ display: "flex", gap: 1.5, flex: 1, minHeight: 0 }}>
        <Box sx={{
          flex: "0 0 150px", minWidth: 130,
          display: "flex", flexDirection: "column", gap: 0.75,
          pr: 1.5, borderRight: 1, borderColor: "divider", overflow: "hidden",
        }}>
          {/* 모든 KPI 를 라벨(위) + 값(아래) 좌측 정렬로 통일 — 대표값(YTD)과 동일 형태 */}
          {stats.map((s, i) => (
            <Box key={s.text}>
              <Typography sx={{ fontSize: FS.SUB, color: "text.secondary", whiteSpace: "nowrap" }}>{s.text}</Typography>
              <Typography sx={{
                fontSize: i === 0 ? FS.KPI : FS.H1,
                fontWeight: i === 0 ? 800 : 700,
                color: i === 0 ? "primary.main" : "text.primary",
                lineHeight: i === 0 ? 1.15 : 1.3,
                whiteSpace: "nowrap",
              }}>
                {i === 0 ? fmtMoney(kpi?.headline ?? 0) : fmtStat(s.value)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <DashboardComposedChart
            data={data}
            yAxes={yAxes}
            tooltipFormatter={(v, name) => (rightLabels.has(name) ? rightFmt(v) : fmtMoney(v))}
          >
            <defs>
              {series.map((s, i) => (typeOf(s) === "AREA" ? (
                <linearGradient key={s.name} id={gradId(s.name)} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colorOf(i)} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={colorOf(i)} stopOpacity={0} />
                </linearGradient>
              ) : null))}
            </defs>
            {series.map((s, i) => isActive(s.name) && renderSeries(s, i))}
          </DashboardComposedChart>
        </Box>
      </Box>

      <SeriesLegend items={legendItems} isActive={isActive} onToggle={onToggleSeries} gap={1.2} />
    </Card>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const Dashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { dashboardMeasures: dashboardMeasure, dashboardTitle } = CodeUtil;
  const { SALES, GROSS_PROFIT, PURCHASE, INVENTORY } = dashboardTitle;
  const mTitles = values(dashboardTitle);

  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));
  const [raw, setRaw] = useState(null);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [expandedKey, setExpandedKey] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configScope, setConfigScope] = useState(null);

  const { snackBarMessageColor: { networkError } } = ColorUtil;

  // 사용자별 차트 설정(z_p13n, pageCd = viewName) — 시리즈 차트 타입 + 활성 여부. 범례와 동일 소스.
  const { chartConfig, saveChartConfig, toggleSeries } =
    useDashboardP13n(viewName, { defaultChartConfig: DEFAULT_CHART_CONFIG });
  const isActive = (k) => chartConfig?.[k]?.activeFlg !== false;

  const openConfig = (key) => { setConfigScope(key); setConfigOpen(true); };

  const retrieveDashBoard = () => {
    dashboardService.get({ currentDate: new ZDate() })
      .then((response) => { if (response.data) setRaw(response.data); })
      .catch((e) => {
        debug(e);
        setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
      });
  };

  useEffect(() => {
    retrieveDashBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 기존 setDatas 로직 — 도메인별 { headline(숫자), stats(라벨/값 배열) } 로 계산.
  const kpiMap = useMemo(() => {
    const datas = raw;
    if (!datas || isEmpty(datas.baseDates)) return {};

    const contents = {
      [SALES]: ["YTD", `Vs. ${t("actual")} + ${t("plan")}`, `Vs. ${t("plan")}`, `Vs. ${t("finance")}`],
      [GROSS_PROFIT]: ["YTD", `Vs. ${t("actual")} + ${t("plan")}`, `Vs. ${t("plan")}`, `Vs. ${t("finance")}`],
      [PURCHASE]: ["YTD", `Vs. ${t("plan")}`, `Vs. ${t("budget_")}`, t("planFillRate"), t("budgetFillRate")],
      [INVENTORY]: [t("current"), `Vs. ${t("target")}`, `Vs. ${t("plan")}`, t("invTurnover")],
    };

    if (sumBy(datas?.finanSaleAmt) === 0) {
      contents[SALES].pop();
      contents[GROSS_PROFIT].pop();
    }

    const firstBaseMon = new ZDate(datas?.baseDates[0])?.getMonth() ?? 1;
    let todayMonth = new ZDate().getMonth();
    if (firstBaseMon > todayMonth) todayMonth = 12 - todayMonth;

    const curreny = userInfo?.currencySymbol;
    const result = {};

    for (const mainTitle of mTitles) {
      let subTitle = [];
      let headline = 0;
      if (isEqual(mainTitle, SALES) || isEqual(mainTitle, GROSS_PROFIT)) {
        for (let i = 0; i < contents[mainTitle].length; i++) {
          const content = contents[mainTitle][i];
          let value = 0;
          const { ACT_SALES_AMT, PLAN_SALES_AMT, FIN_SALES_AMT, LAND_SALES_AMT } = dashboardMeasure[SALES];
          const { ACT_GROSS_PROFIT, PLAN_GROSS_PROFIT, FIN_GROSS_PROFIT, LAND_GROSS_PROFIT } = dashboardMeasure[GROSS_PROFIT];
          const actNm = isEqual(mainTitle, SALES) ? ACT_SALES_AMT.name : ACT_GROSS_PROFIT.name;
          const planNm = isEqual(mainTitle, SALES) ? PLAN_SALES_AMT.name : PLAN_GROSS_PROFIT.name;
          const finNm = isEqual(mainTitle, SALES) ? FIN_SALES_AMT.name : FIN_GROSS_PROFIT.name;
          const landNm = isEqual(mainTitle, SALES) ? LAND_SALES_AMT.name : LAND_GROSS_PROFIT.name;

          const actualSum = getSum(datas[actNm]);
          const planSum = getSum(datas[planNm]);
          const financeSum = getSum(datas[finNm]);
          const landing = datas[landNm];
          const landingSum = landing ? landing[landing.length - 1] : 0;
          if (i === 0) {
            headline = checkNumberValue(actualSum);
            value = `${curreny}${numberUtil.formatNumber(headline)}`;
          } else if (i === 1) {
            value = `${floor(checkNumberValue(divide(actualSum, landingSum)) * 100)}%`;
          } else if (i === 2) {
            value = `${floor(checkNumberValue(divide(actualSum, planSum)) * 100)}%`;
          } else if (i === 3) {
            value = `${floor(checkNumberValue(divide(actualSum, financeSum)) * 100)}%`;
          }
          subTitle.push({ text: content, value });
        }
      } else if (isEqual(mainTitle, PURCHASE)) {
        subTitle = [];
        for (let i = 0; i < contents[mainTitle].length; i++) {
          const content = contents[mainTitle][i];
          let value = 0;
          const { ACT_PC_AMT, PLAN_PC_AMT, PC_BUDGET_AMT } = dashboardMeasure[PURCHASE];
          const actualSum = getSum(datas[ACT_PC_AMT.name]);
          const planSum = getSum(datas[PLAN_PC_AMT.name]);
          const budgetSum = getSum(datas[PC_BUDGET_AMT.name]);
          const planSumCurrent = getSumUntilCurrentMonth(datas[PLAN_PC_AMT.name], datas.baseDates);
          const budgetSumCurrent = getSumUntilCurrentMonth(datas[PC_BUDGET_AMT.name], datas.baseDates);

          if (i === 0) {
            headline = checkNumberValue(actualSum);
            value = `${curreny}${numberUtil.formatNumber(headline)}`;
          } else if (i === 1) {
            value = `${floor(checkNumberValue(divide(actualSum, planSum)) * 100)}%`;
          } else if (i === 2) {
            value = `${floor(checkNumberValue(divide(actualSum, budgetSum)) * 100)}%`;
          } else if (i === 3) {
            value = `${floor(checkNumberValue(divide(actualSum, planSumCurrent)) * 100)}%`;
          } else if (i === 4) {
            value = `${floor(checkNumberValue(divide(actualSum, budgetSumCurrent)) * 100)}%`;
          }
          subTitle.push({ text: content, value });
        }
      } else if (isEqual(mainTitle, INVENTORY)) {
        subTitle = [];
        for (let i = 0; i < contents[mainTitle].length; i++) {
          const content = contents[mainTitle][i];
          let value = 0;
          const { ACT_STOCK_AMT, PLAN_STOCK_AMT, TARGET_INV_AMT } = dashboardMeasure[INVENTORY];
          const { ACT_SALES_AMT } = dashboardMeasure[SALES];
          const actualSum = getSum(datas[ACT_STOCK_AMT.name]);
          const planSum = getSum(datas[PLAN_STOCK_AMT.name]);
          const targetSum = getSum(datas[TARGET_INV_AMT.name]);
          if (i === 0) {
            const stocksUtilCurrentMon = cloneDeep(datas[ACT_STOCK_AMT.name]).slice(0, todayMonth);
            headline = checkNumberValue(findLastNonNullValue(stocksUtilCurrentMon));
            value = `${curreny}${numberUtil.formatNumber(headline)}`;
          } else if (i === 1) {
            value = `${floor(checkNumberValue(divide(actualSum, targetSum)) * 100)}%`;
          } else if (i === 2) {
            value = `${floor(checkNumberValue(divide(actualSum, planSum)) * 100)}%`;
          } else if (i === 3) {
            const thisMonth = new ZDate().getMonth();
            const totalDays = getTotalDaysUntilMonth(thisMonth);
            const turnover = checkNumberValue(getSum(datas?.[ACT_SALES_AMT.name]) / ((datas?.[ACT_STOCK_AMT.name][0] + datas?.[ACT_STOCK_AMT.name][thisMonth]) / 2));
            const days = turnover > 0 ? totalDays / turnover : 0;
            value = [turnover.toFixed(2), days.toFixed(0)];
          }
          subTitle.push({ text: content, value });
        }
      }
      result[mainTitle] = { headline, stats: subTitle };
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  const periods = useMemo(
    () => (raw?.baseDates ?? []).map((d) => dateUtils.formatDate(d, "yyyy-MM")),
    [raw],
  );

  const fmtMoney = useMemo(() => makeFmtMoney(raw?.currencyCd), [raw]);
  const financeAvailable = sumBy(raw?.finanSaleAmt) !== 0;
  const period = periods.length ? { from: periods[0], to: periods[periods.length - 1] } : null;

  const renderTrend = (section) => (
    <TrendCard
      key={section.key}
      section={section}
      raw={raw}
      periods={periods}
      financeAvailable={financeAvailable}
      fmtMoney={fmtMoney}
      kpi={kpiMap[section.key]}
      chartConfig={chartConfig}
      isActive={isActive}
      onToggleSeries={toggleSeries}
      onOpenConfig={() => openConfig(section.key)}
      expanded={expandedKey === section.key}
      onToggleExpand={() => setExpandedKey((k) => (k === section.key ? null : section.key))}
    />
  );

  const configSection = SECTIONS.find((s) => s.key === configScope);
  const configMeasures = configSection
    ? configSection.series
        .filter((s) => !(s.financeGated && !financeAvailable))
        // color = 차트 시리즈색(colorOf(i)=colorSet(i))과 동일 — 필터/순서가 차트의 series 와 같아 인덱스 일치
        .map((s, i) => ({ key: s.name, label: t(s.name), hasActive: true, color: ColorUtil.colorSet(i) }))
    : [];

  if (!raw || isEmpty(kpiMap)) return null;

  return (
    <DashboardPage>
      <DashboardTopBar period={period} />

      {/* 도메인별 카드 — 좌 KPI / 우 트렌드 차트 (Option C) */}
      {expandedKey ? (
        <DashboardRow>{renderTrend(SECTIONS.find((s) => s.key === expandedKey))}</DashboardRow>
      ) : (
        <>
          <DashboardRow>
            {renderTrend(SECTIONS[0])}
            {renderTrend(SECTIONS[1])}
          </DashboardRow>
          <DashboardRow>
            {renderTrend(SECTIONS[2])}
            {renderTrend(SECTIONS[3])}
          </DashboardRow>
        </>
      )}

      <ChartConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        config={chartConfig}
        onSave={(cfg) => { saveChartConfig(cfg); setConfigOpen(false); }}
        measures={configMeasures}
        chartTypes={CHART_TYPES}
      />

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </DashboardPage>
  );
};

export default Dashboard;
